import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { API } from "@/App";
import logger from "@/utils/logger";
import { toast } from "sonner";
import adminApi from "./adminApi";
import { StatusBadge } from "./StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreVertical, Trash2, CheckCircle, XCircle, Bell } from "lucide-react";

const BookingsManagement = () => {
  const { t } = useTranslation();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const fetchBookings = useCallback(async () => {
    try {
      const response = await adminApi.get(`${API}/bookings`);
      setBookings(response.data);
    } catch (error) {
      logger.error("Error fetching bookings:", error);
      toast.error(t("admin.dashboard.toast.bookingUpdateError"));
    } finally {
      setLoading(false);
    }
  }, [t, API, adminApi]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const updateStatus = async (bookingId, status) => {
    try {
      await adminApi.put(`${API}/bookings/${bookingId}`, { status });
      toast.success(t("admin.dashboard.toast.bookingUpdated", { status }));
      fetchBookings();
    } catch {
      toast.error(t("admin.dashboard.toast.bookingUpdateError"));
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm(t("admin.dashboard.confirmDelete.booking"))) return;
    try {
      await adminApi.delete(`${API}/bookings/${bookingId}`);
      toast.success(t("admin.dashboard.toast.bookingDeleted"));
      fetchBookings();
    } catch {
      toast.error(t("admin.dashboard.toast.bookingDeleteError"));
    }
  };

  const filteredBookings = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);

  const renderTable = () => {
    if (loading) {
      return <div className="flex justify-center py-12"><div className="spinner"></div></div>;
    }
    if (filteredBookings.length === 0) {
      return <p className="text-[#6B705C] text-center py-12">{t("admin.dashboard.noResults")}</p>;
    }
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("admin.dashboard.table.customer")}</TableHead>
            <TableHead>{t("admin.dashboard.table.event")}</TableHead>
            <TableHead>{t("admin.dashboard.table.date")}</TableHead>
            <TableHead>{t("admin.dashboard.table.guestCount")}</TableHead>
            <TableHead>{t("admin.dashboard.table.status")}</TableHead>
            <TableHead>{t("admin.dashboard.table.actions")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredBookings.map((booking) => (
            <TableRow key={booking.id} data-testid={`booking-row-${booking.id}`}>
              <TableCell>
                <div>
                  <p className="font-medium">{booking.customer_name}</p>
                  <p className="text-xs text-[#6B705C]">{booking.customer_email}</p>
                </div>
              </TableCell>
              <TableCell>{booking.event_type}</TableCell>
              <TableCell>
                <div className="flex items-center gap-1.5">
                  {booking.event_date}
                  {booking.status === "confirmed" && booking.reminder_sent && (
                    <Bell className="w-3.5 h-3.5 text-[#2D6A4F]" title="Reminder sent" data-testid={`reminder-sent-${booking.id}`} />
                  )}
                </div>
              </TableCell>
              <TableCell>{booking.guest_count}</TableCell>
              <TableCell><StatusBadge status={booking.status} /></TableCell>
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" data-testid={`booking-actions-${booking.id}`}>
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => updateStatus(booking.id, "confirmed")}>
                      <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                      {t("admin.dashboard.actions.confirm")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => updateStatus(booking.id, "completed")}>
                      <CheckCircle className="w-4 h-4 mr-2 text-blue-600" />
                      {t("admin.dashboard.actions.markComplete")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => updateStatus(booking.id, "cancelled")}>
                      <XCircle className="w-4 h-4 mr-2 text-red-600" />
                      {t("admin.dashboard.actions.cancel")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => deleteBooking(booking.id)} className="text-red-600">
                      <Trash2 className="w-4 h-4 mr-2" />
                      {t("admin.dashboard.actions.delete")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <div data-testid="bookings-management" className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-[#3D405B] font-['Playfair_Display']">{t("admin.dashboard.bookings")}</h1>
          <p className="text-[#6B705C]">{t("admin.dashboard.manageBookings")}</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40" data-testid="booking-filter">
            <SelectValue placeholder={t("admin.dashboard.filter.all")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("admin.dashboard.filter.all")}</SelectItem>
            <SelectItem value="pending">{t("admin.dashboard.filter.pending")}</SelectItem>
            <SelectItem value="confirmed">{t("admin.dashboard.filter.confirmed")}</SelectItem>
            <SelectItem value="completed">{t("admin.dashboard.filter.completed")}</SelectItem>
            <SelectItem value="cancelled">{t("admin.dashboard.filter.cancelled")}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Card className="bg-white border-0 shadow-sm">
        <CardContent className="p-0">{renderTable()}</CardContent>
      </Card>
    </div>
  );
};

export default BookingsManagement;
